import {ChangeEvent} from 'react';

const GROUP_SEPARATOR = ' ';

const isSeparator = (char: string) => /\s/.test(char);

export function formatAccountNumber(value: string): string {
    return (
        value
            .toUpperCase()
            .replace(/\s/g, '')
            .match(/.{1,4}/g)
            ?.join(GROUP_SEPARATOR) ?? ''
    );
}

export function formatSwift(value: string): string {
    return value.replace(/\s/g, '').toUpperCase();
}

type FormattedField = {
    value: string;
    onChange: (value: string) => void;
};

/**
 * Re-formats the value on every edit, keeping the caret where the user left it.
 * A controlled input rewritten by the formatter would otherwise throw the caret
 * to the end of the line on each keystroke.
 */
export function withFormatting<F extends FormattedField>(field: F, format: (value: string) => string) {
    return {
        ...field,
        onChange: (event: ChangeEvent<HTMLInputElement>) => {
            const input = event.currentTarget;
            const {value, caret} = reformat(field.value, input.value, input.selectionEnd ?? input.value.length, format);

            field.onChange(value);
            requestAnimationFrame(() => {
                // The formatted value may equal the current one (typing a separator, for
                // instance), then React re-renders nothing and the raw value stays in the DOM.
                if (input.value !== value) {
                    input.value = value;
                }
                input.setSelectionRange(caret, caret);
            });
        },
    };
}

export function reformat(previous: string, raw: string, caret: number, format: (value: string) => string) {
    if (isSeparatorRemoval(previous, raw, caret)) {
        // Formatting would put the separator straight back, so nothing would be
        // deleted. Drop the character in front of it instead, as the user intended.
        raw = raw.slice(0, caret - 1) + raw.slice(caret);
        caret -= 1;
    }
    const value = format(raw);
    return {value, caret: caretAfter(value, raw, caret)};
}

function isSeparatorRemoval(previous: string, raw: string, caret: number) {
    return (
        caret > 0 &&
        previous.length === raw.length + 1 &&
        isSeparator(previous[caret]) &&
        previous.slice(0, caret) === raw.slice(0, caret) &&
        previous.slice(caret + 1) === raw.slice(caret)
    );
}

/**
 * Position in the formatted value holding as many meaningful characters
 * as the raw value holds before the caret.
 */
function caretAfter(formatted: string, raw: string, rawCaret: number) {
    let significant = 0;
    for (let i = 0; i < rawCaret && i < raw.length; i++) {
        if (!isSeparator(raw[i])) {
            significant++;
        }
    }

    let position = 0;
    for (; position < formatted.length && significant > 0; position++) {
        if (!isSeparator(formatted[position])) {
            significant--;
        }
    }
    return position;
}
